import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import every from 'lodash/every';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNull from 'lodash/isNull';
import FlatButton from 'material-ui/FlatButton';
import Dialog from 'material-ui/Dialog';
import { publisherManager, userManager } from '../../helpers/managers';
import {
  createStripeCustomer,
  createStripeCardSource,
  updateUserPaymentCard,
  updateUserBilling,
  parseExpiryMonthAndYear,
} from '../../helpers/payments';
import { BaseButton } from '../../styles/styled';
import Loading from '../../components/Loading';
import Colors from '../../styles/colors';
import BasicFields from './BasicFields';
import PoweredByStripe from './PoweredByStripe';

class CustomerCard extends Component {
  constructor(props) {
    super(props);

    const {
      billing: {
        addressLine1 = '',
        addressCity = '',
        addressState = '',
        addressZip = '',
        addressCountry = '',
      },
      card: {
        brand: cardBrand = '',
        last4: cardLast4 = '',
      },
    } = props;

    this.state = {
      addressLine1,
      addressCity,
      addressState,
      addressZip,
      addressCountry,
      cardType: '',
      cardNumber: '',
      cardExpMonth: '',
      cardExpYear: '',
      cardCVC: '',
      cardBrand,
      cardLast4,

      error: '',
      updating: false,
      mode: 'none',
      forceNewCustomer: false,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.mode !== this.state.mode) {
      this.setState({
        error: '',
      });
    }
  }

  onModalClose = () => {
    this.showModalWithMode('none');
  };

  allFieldsValid = (fields) => {
    return every(fields.map(field => {
      return this.state[field] !== '';
    }), Boolean);
  };

  getBillingFields = () => {
    const fields = {};
    [
      'addressLine1',
      'addressCity',
      'addressState',
      'addressZip',
      'addressCountry',
    ].forEach((field) => {
      fields[field] = '';

      // only get data if field is shown
      if (this.props.showBillingFields[field]) {
        fields[field] = this.state[field];
      }
    });

    return fields;
  };

  getCardFields = () => {
    const {
      cardType,
      cardNumber,
      cardExpMonth,
      cardExpYear,
      cardCVC,
    } = this.state;

    return {
      cardType,
      cardNumber,
      cardExpMonth,
      cardExpYear,
      cardCVC,
    };
  };

  onUpdateClick = () => {
    const { userId, sourceId, customerId, onError, onBillingUpdate, onBeforeCardCreated, onNewCardCreated } = this.props;
    const { error, updating, mode, forceNewCustomer } = this.state;
    this.setState({
      updating: true,
      error: '',
    }, async () => {
      try {
        if (!forceNewCustomer && isEmpty(customerId)) {
          throw new Error('Invalid customer details.');
        }

        if (mode === 'newcard') {
          const isAllValid = this.allFieldsValid([
            ...Object.keys(this.getBillingFields()),
            ...Object.keys(this.getCardFields()),
          ]);

          if (!isAllValid) {
            throw new Error('Some of the required fields are invalid.');
          }

          // create new source for card
          const fields = { ...this.getBillingFields(), ...this.getCardFields() };

          // force create customer if set
          let ncustomerId = customerId;
          if (forceNewCustomer && isEmpty(customerId)) {
            const fcustomer = await new Promise(async (resolve, reject) => {
              try {
                // create source or token
                const source = await createStripeCardSource(fields);
                if (!('id' in source) || isEmpty(source.id)) {
                  throw new Error('Invalid token while creating customer, try again.');
                }

                const { data } = await createStripeCustomer(source.id, userId);
                resolve(data);
              } catch (err) {
                reject(err);
              }
            });

            // assign new customer id
            ncustomerId = fcustomer.id;
          }

          // call local api to update card
          try {
            const { card, source } = await new Promise(async (resolve, reject) => {
              try {
                // create source or token
                const source = await createStripeCardSource(fields);
                if (!('id' in source) || isEmpty(source.id)) {
                  throw new Error('Invalid token while creating card, try again.');
                }

                // call an event before it creates the card, it may return a promise
                const fn = onBeforeCardCreated({ source, card, fields });
                await Promise.resolve(fn);

                const { data } = await updateUserPaymentCard({
                  source: source.id,
                  customer: ncustomerId,
                  userId,
                });

                resolve({
                  card: data,
                  source,
                });
              } catch (err) {
                reject(err);
              }
            });

            // upate user stripe_id and publisher stripe_customer_id if user is owner for publisher
            if (!isEmpty(card) && !isEmpty(card.id)) {
              const customerId = card.id;
              await userManager.saveProp(userId, 'stripe_id', customerId);
              const publisherID = await userManager.getProp(userId, 'publisherID');
              if (!isEmpty(publisherID)) {
                const owner = await publisherManager.getProp(publisherID, 'owner');
                if (isEqual(userId,owner)) {
                  await publisherManager.saveProp(publisherID, 'stripe_customer_id', customerId);
                }
              }
            }

            // pass the new source and use it for payment
            onNewCardCreated({ source, card, fields });

            // close modal
            this.setState({
              updating: false,
            }, () => {
              this.onModalClose();
            });
          } catch (err) {
            const errMsg = err && err.response && err.response.data;
            console.log('Error while updating new card: ', errMsg, err);
            throw new Error('There is an error while creating new card. Please try again later.');
          }
        } else if (mode === 'billing') {
          try {
            const isAllValid = this.allFieldsValid([
              ...Object.keys(this.getBillingFields()),
            ]);

            if (!isAllValid) {
              throw new Error('Some of the required fields are invalid.');
            }

            // update billing using existing source
            const fields = { ...this.getBillingFields() };
            await updateUserBilling({
              source: sourceId,
              customer: customerId,
              userId,
              billing: fields,
            });

            // pass the updated fields and use it for payment
            onBillingUpdate(fields);

            // close modal
            this.setState({
              updating: false,
            }, () => {
              this.onModalClose();
            });
          } catch (err) {
            const errMsg = err && err.response && err.response.data;
            console.log('Error while updating billing details: ', errMsg, err);
            throw new Error('There is an error while updating billing details. Please try again later.');
          }
        }
      } catch (e) {
        this.setState({
          updating: false,
          error: e.message,
        });
      }
    });
  };

  handlePaymentCard = (statekey) => {
    return value => {
      if (statekey === 'expiry') {
        const { expMonth, expYear } = parseExpiryMonthAndYear(value);
        return this.setState({
          cardExpMonth: expMonth,
          cardExpYear: expYear,
        });
      }

      this.setState({
        error: '',
        [statekey]: value,
      });
    };
  };

  showModalWithMode = (mode, extras = {}) => {
    this.setState({
      mode,
      ...extras,
    });
  };

  editBilling = () => {
    this.showModalWithMode('billing');
  };

  setupNewCard = (forceNewCustomer = false) => {
    this.showModalWithMode('newcard', { forceNewCustomer });
  };

  renderFormFields = () => {
    const {
      checkoutEmail,
      addressLine1,
      addressCity,
      addressState,
      addressZip,
      addressCountry,
      cardType,
      cardNumber,
      cardExpMonth,
      cardExpYear,
      cardCVC,
      mode,
    } = this.state;

    return (
      <BasicFields
        showEmail={false}
        showCardFields={mode !== 'billing'}
        addressLine1={addressLine1}
        addressCity={addressCity}
        addressState={addressState}
        addressZip={addressZip}
        addressCountry={addressCountry}
        cardType={cardType}
        cardNumber={cardNumber}
        cardExpMonth={cardExpMonth}
        cardExpYear={cardExpYear}
        cardCVC={cardCVC}
        onChangeField={this.handlePaymentCard}
        showBillingFields={this.props.showBillingFields}
      />
    );
  }

  renderUpdateDetailsPopup() {
    const { customActionButtons } = this.props;
    const { error, mode, updating } = this.state;

    const modes = {
      none: {
        btnLabel: 'Update',
        title: 'Update Details',
      },
      newcard: {
        btnLabel: 'Update & Pay',
        title: 'New Card Details',
      },
      billing: {
        btnLabel: 'Update',
        title: 'Update Billing Details',
      },
      ...customActionButtons,
    };

    const modeDetail = modes[mode];
    const renderUpdateBtn = () => {
      const btnContent = updating ? (
        <Loading
          text="PROCESSING"
          type="circle"
          align="center"
          size={20}
          fontSize={1.4}
          color="#fff"
          fill="#fff"
          textStyle={{ margin: '0 5px' }}
        />
      ) : modeDetail.btnLabel;

      return (
        <UpdateButton
          onClick={this.onUpdateClick}
          disabled={updating}
          className={`btn text-white text-medium ${updating ? 'disabled' : ''}`}
          primaryColor="#00BCD4"
        >
        {btnContent}
        </UpdateButton>
      );
    };

    return (
      <Dialog
        title={modeDetail.title}
        actions={[
          <CancelButton
            disabled={updating}
            onClick={this.onModalClose}
            className="btn text-medium margin-two-right naked"
          >
            Cancel
          </CancelButton>,
          renderUpdateBtn()
        ]}
        modal={updating}
        open={mode!=='none'}
        onRequestClose={this.onModalClose}
        contentStyle={{ width: '95%', maxWidth: 560, transform: 'translate(0px, 15px)' }}
        bodyStyle={{ overflowY: 'auto' }}
        actionsContainerStyle={{
          padding: '20px 24px',
          border: '1px solid #f6f6f6',
        }}
        autoScrollBodyContent
      >
        {!isEmpty(error) && (
          <div className="row">
            <div className="col-lg-12 col-md-12 col-xs-12">
              <div className="alert alert-danger text-medium" role="alert">{error}</div>
            </div>
          </div>
        )}
        {this.renderFormFields()}
        {mode !== 'billing' && (
          <PoweredByStripe />
        )}
      </Dialog>
    );
  }

  renderBilling = () => {
    const {
      addressLine1,
      addressCity,
      addressState,
      addressZip,
      addressCountry,
    } = this.state;

    const nonEmptyFields = [
      addressLine1,
      addressCity,
      addressState,
      addressZip,
      addressCountry
    ].every(field => !isEmpty(field));

    if (!nonEmptyFields) {
      return null;
    }

    return (
      <React.Fragment>
        <div className="text-medium">{addressLine1}</div>
        <span className="text-medium">
          {addressCity}
        </span>
        <span className="text-medium">
          {addressCity ? `, ${addressState}` : addressState}
        </span>
        <span className="text-medium">
          {` ${addressZip}, ${addressCountry}`}
        </span>
      </React.Fragment>
    );
  };

  render() {
    const { customRenderer } = this.props;
    const { cardBrand, cardLast4 } = this.state;

    // if there's a custom renderer use it
    const renderer = customRenderer();
    if (!isNull(renderer)) {
      const content = renderer({
        editBilling: this.editBilling,
        setupNewCard: this.setupNewCard,
        renderBilling: this.renderBilling,
      });

      return (
        <React.Fragment>
          {content}
          {this.renderUpdateDetailsPopup()}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment>
        <div className="row padding-two-bottom">
          <div className="col-lg-12">
            <span className="text-large font-weight-600 margin-four-right">Billing Details</span>
            <ButtonText className="text-medium font-weight-600" onClick={this.editBilling}>Edit</ButtonText>
            <div className="padding-two-top">
              {this.renderBilling()}
            </div>
          </div>
        </div>
        <div className="row padding-five-bottom">
          <div className="col-lg-7 col-md-7 col-sm-12 col-xs-12">
            <strong>{cardBrand} {` **** ***** ${cardLast4}`}</strong>
          </div>
          <div className="col-lg-5 col-md-5 col-sm-12 col-xs-12 text-right">
            <ButtonText onClick={this.setupNewCard}>Pay with another card?</ButtonText>
          </div>
        </div>
        {this.renderUpdateDetailsPopup()}
      </React.Fragment>
    );
  }
}

const ButtonText = styled.span`
  color: ${Colors.mainOrange};
  cursor: pointer;
`;

const CancelButton = styled(BaseButton)`
  text-transform: uppercase;
`;

const UpdateButton = styled(BaseButton)`
  text-transform: uppercase;
`;

CustomerCard.propTypes = {
  userId: PropTypes.string,
  sourceId: PropTypes.string,
  customerId: PropTypes.string,
  billing: PropTypes.object,
  card: PropTypes.object,
  customActionButtons: PropTypes.object,
  onError: PropTypes.func,
  onBeforeCardCreated: PropTypes.func,
  onNewCardCreated: PropTypes.func,
  onBillingUpdate: PropTypes.func,
  customRenderer: PropTypes.func,
  showBillingFields: PropTypes.object,
};

CustomerCard.defaultProps = {
  userId: '',
  sourceId: '',
  customerId: '',
  billing: {},
  card: {},
  customActionButtons: {},
  onError: () => {},
  onBeforeCardCreated: () => {},
  onNewCardCreated: () => {},
  onBillingUpdate: () => {},
  customRenderer: () => null,
  showBillingFields: {
    addressLine1: true,
    addressCity: true,
    addressState: true,
    addressZip: true,
    addressCountry: true,
  },
};

export default CustomerCard;
